import type { BlockSchema, JSONSchemaNode } from '@/lib/api/types';

/**
 * Schema resolution for the form engine (plan §4.7). Detects which widget to render for a
 * given JSON Schema node. Adding a backend block type needs NO console change because the
 * picker + form are generated from the schema registry (spec §9.1).
 */
export type WidgetKind =
  | 'i18n'
  | 'i18n-rich'
  | 'media'
  | 'entity'
  | 'ref-slug'
  | 'string'
  | 'number'
  | 'boolean'
  | 'enum'
  | 'object'
  | 'geo'
  | 'map'
  | 'array'
  | 'unknown';

export interface ResolvedField {
  name: string;
  schema: JSONSchemaNode;
  widget: WidgetKind;
  label: string;
  required: boolean;
  /** For entity widget: which collection to pick from (from x-entity hint). */
  entity?: string;
}

/** Resolve a `$ref` like "#/$defs/I18n" against the root schema's $defs. */
export function deref(node: JSONSchemaNode, defs: Record<string, JSONSchemaNode>): JSONSchemaNode {
  if (node.$ref) {
    const name = node.$ref.split('/').pop()!;
    const target = defs[name];
    if (target) return { ...target, ...node, $ref: undefined };
  }
  return node;
}

/** A leaf whose value is a localized *string* (block i18n() emits `{type:string}`). */
function isStringLeaf(node: JSONSchemaNode | undefined): boolean {
  if (!node) return false;
  if (node.$ref) return /I18n(Rich)?$/.test(node.$ref);
  return Array.isArray(node.type) ? node.type.includes('string') : node.type === 'string';
}

/**
 * Is this node (after deref) the I18n / I18nRich shape — i.e. a `{en, ar}` pair of
 * *strings*? The string check matters: a collection doc carries locale at the top of
 * a content block (`card: {en: {...}, ar: {...}}`), which also has exactly en/ar keys
 * but holds objects — that must render as a nested form, not a two-line text field.
 */
function isI18nRef(node: JSONSchemaNode): boolean {
  if (node.$ref) return /I18n(Rich)?$/.test(node.$ref);
  const props = node.properties;
  return (
    node.type === 'object' &&
    !!props &&
    'en' in props &&
    'ar' in props &&
    Object.keys(props).length === 2 &&
    isStringLeaf(props.en) &&
    isStringLeaf(props.ar)
  );
}

/**
 * A dynamic-key map: an object with a schema-valued `additionalProperties` and no
 * fixed `properties`. Values share one shape; the keys are content the editor names
 * (e.g. a family's variant slugs). `additionalProperties: true` is NOT a map — that
 * is just a permissive object.
 */
function isMapNode(node: JSONSchemaNode): boolean {
  const ap = node.additionalProperties;
  const hasProps = !!node.properties && Object.keys(node.properties).length > 0;
  return effectiveType(node) === 'object' && !hasProps && typeof ap === 'object' && ap !== null;
}

/** The value schema of a map node (what each entry conforms to). */
export function mapValueSchema(node: JSONSchemaNode): JSONSchemaNode {
  return typeof node.additionalProperties === 'object' && node.additionalProperties
    ? node.additionalProperties
    : { type: 'object' };
}

function isRich(node: JSONSchemaNode): boolean {
  // Either the console's own `$ref: I18nRich` (static config) or the backend's
  // `x-rich` UI hint on a plain `{en,ar}` leaf (served schema) selects the
  // multi-line editor for long-form copy like an article body.
  if (node.$ref) return /I18nRich$/.test(node.$ref);
  return node['x-rich'] === true;
}

/**
 * The effective scalar type, collapsing a nullable union (`["string","null"]`) to its non-null
 * member. Backends emit unions for optional fields (e.g. `background_image_id`); without this
 * they'd fall through to the raw-JSON fallback.
 */
function effectiveType(node: JSONSchemaNode): string | undefined {
  if (Array.isArray(node.type)) return node.type.find((x) => x !== 'null') ?? 'null';
  return node.type;
}

export function detectWidget(node: JSONSchemaNode): WidgetKind {
  if (node['x-widget'] === 'geo') return 'geo';
  if (isI18nRef(node)) return isRich(node) ? 'i18n-rich' : 'i18n';
  if (node['x-ref-slug']) return 'ref-slug';
  if (node.format === 'uuid') return node['x-entity'] ? 'entity' : 'media';
  if (node.enum && node.enum.length > 0) return 'enum';
  if (isMapNode(node)) return 'map';
  switch (effectiveType(node)) {
    case 'string':
    case 'null':
      return 'string';
    case 'number':
    case 'integer':
      return 'number';
    case 'boolean':
      return 'boolean';
    case 'object':
      return 'object';
    case 'array':
      return 'array';
    default:
      return 'unknown';
  }
}

/** Field-name convention: a singular `*_id` reference points at a media asset. */
export function isMediaRefName(name: string): boolean {
  return /_id$/.test(name) && !/_ids$/.test(name);
}

function titleCaseFallback(name: string): string {
  // Sentence-case label fallback from a snake/camel key (no Title Case in headlines, but
  // field labels read as sentence fragments).
  const spaced = name.replace(/[_-]+/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
}

/** Resolve the ordered field list for an object schema. */
export function resolveFields(
  schema: BlockSchema | JSONSchemaNode,
  defs: Record<string, JSONSchemaNode> = {},
): ResolvedField[] {
  const allDefs = { ...(schema as BlockSchema).$defs, ...defs };
  const required = new Set(schema.required ?? []);
  const props = schema.properties ?? {};
  return Object.entries(props).map(([name, raw]) => {
    const node = deref(raw, allDefs);
    let widget = detectWidget(node);
    // A plain `*_id` string with no explicit format/entity hint is, by convention, a media
    // reference — render the asset picker instead of a bare text box (or raw-JSON fallback).
    if ((widget === 'string' || widget === 'unknown') && !node.enum && isMediaRefName(name)) {
      widget = 'media';
    }
    return {
      name,
      schema: node,
      widget,
      label: node.title ?? titleCaseFallback(name),
      required: required.has(name),
      entity: node['x-entity'],
    };
  });
}

/** Build empty defaults for a schema (used when adding a new section). */
export function emptyValue(node: JSONSchemaNode, defs: Record<string, JSONSchemaNode>): unknown {
  const resolved = deref(node, defs);
  if (resolved.default !== undefined) return resolved.default;
  const w = detectWidget(resolved);
  switch (w) {
    case 'i18n':
    case 'i18n-rich':
      return { en: '', ar: '' };
    case 'boolean':
      return false;
    case 'number':
      return undefined;
    case 'geo':
      return undefined;
    case 'array':
      return [];
    case 'map':
      return {};
    case 'object': {
      const out: Record<string, unknown> = {};
      for (const [k, child] of Object.entries(resolved.properties ?? {}))
        out[k] = emptyValue(child, defs);
      return out;
    }
    default:
      return '';
  }
}
